import React, { useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine // Add this import
} from 'recharts';
import { Box, Typography } from '@mui/material';

const BarGraph = ({
  data = [],
  xAxis = 'name',
  yAxis = 'value',
  title = 'Bar Chart',
  height = 340, // Increased default height
  color = '#2E8B57',
  showLegend = false,
  barSize = 20, // Increased bar size
  // Axis label props
  xAxisLabel = '',
  yAxisLabel = '',
  // Halt chart specific props
  inactiveKey = null,
  deadKey = null,
  inactiveColor = '#eaff00ff',
  deadColor = '#FF0000',
  // Trip chart specific props
  completedKey = null,
  delayedKey = null,
  abortedKey = null,
  completedColor = '#28a745',
  delayedColor = '#ffc107',
  abortedColor = '#dc3545',
  // Alternative naming for trip charts
  onTimeKey = null,
  onTimeColor = '#28a745',
  // Add maxSpeed prop
  maxSpeed = null,
  isSpeedChart = false,
  // Add new props for reference line
  showReferenceLine = false,
  referenceLineY = 20,
  referenceLineLabel = 'Speed Limit',
  referenceLineColor = 'red'
}) => {
  const isHaltChart = inactiveKey && deadKey;
  const isTripChart = completedKey && delayedKey;
  const isOnTimeDelayedChart = onTimeKey || (completedKey && delayedKey);
  const isGroupedChart = isHaltChart || isTripChart || isOnTimeDelayedChart;

  // Use onTimeKey if provided, otherwise use completedKey
  const actualOnTimeKey = onTimeKey || completedKey;
  const actualOnTimeColor = onTimeColor || completedColor;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold', color: '#333' }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '4px 0', color: entry.color }}>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const formatYAxisTick = useCallback((value) => {
    return Math.round(value);
  }, []);

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      '& .recharts-legend-wrapper': {
        position: 'relative !important',
        height: 'auto !important',
        marginBottom: '10px'
      }
    }}>
      <Typography
        variant="h6"
        sx={{
          textAlign: 'center',
          mb: 0.5, // Reduced bottom margin
          color: '#183059',
          fontWeight: 600,
          fontSize: '16px'
        }}
      >
        {title}
      </Typography>

      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          margin={{ top: 5, right: 20, left: 20, bottom: 45 }}
          barCategoryGap={0}  // Change this from 12 to 0
          barGap={0}  // Change this from 6 to 0
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
          <XAxis
            dataKey={xAxis}
            tick={{ fontSize: 11, fill: '#666' }}
            angle={-45}
            textAnchor="end"
            height={34} // Reduced height
            interval={0}
            label={{ 
              value: xAxisLabel, 
              position: 'insideBottom', 
              offset: -40, // Reduced offset
              style: { textAnchor: 'middle', fontSize: '11px', fill: '#666' }
            }}
          />
          <YAxis 
            tick={{ fontSize: 11, fill: '#666' }}
            tickFormatter={formatYAxisTick}
            allowDecimals={false}
            width={40}
            domain={[0, 'auto']} // This will ensure bars start from 0 and extend to max value
            label={{ 
              value: yAxisLabel, 
              angle: -90, 
              position: 'insideLeft',
              offset: -5,
              style: { textAnchor: 'middle', fontSize: '11px', fill: '#666' }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          {/* Move Legend above the chart */}
          {(showLegend || isGroupedChart) && (
            <Legend
              verticalAlign="top"
              height={36}
              wrapperStyle={{
                paddingTop: '10px',
                paddingBottom: '10px',
                fontSize: '11px'
              }}
            />
          )}

          {isHaltChart ? (
            // Halt chart bars
            <>
              <Bar
                dataKey={inactiveKey}
                fill={inactiveColor}
                name="Inactive"
                barSize={barSize}
              />
              <Bar
                dataKey={deadKey}
                fill={deadColor}
                name="Dead"
                barSize={barSize}
              />
            </>
          ) : isOnTimeDelayedChart ? (
            // Trip chart bars (On Time/Delayed or Completed/Delayed)
            <>
              <Bar
                dataKey={actualOnTimeKey}
                fill={actualOnTimeColor}
                name="On Time"
                barSize={barSize}
              />
              <Bar
                dataKey={delayedKey}
                fill={delayedColor}
                name="Delayed"
                barSize={barSize}
              />
              {abortedKey && (
                <Bar
                  dataKey={abortedKey}
                  fill={abortedColor}
                  name="Aborted"
                  barSize={barSize}
                />
              )}
            </>
          ) : (
            // Single bar chart
            <Bar
              dataKey={yAxis}
              fill={color}
              name={yAxis.charAt(0).toUpperCase() + yAxis.slice(1)}
              barSize={barSize}
            />
          )}

          {/* Add ReferenceLine for speed limit */}
          {showReferenceLine && (
            <ReferenceLine 
              y={referenceLineY}
              stroke={referenceLineColor}
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{
                position: 'right',
                fill: referenceLineColor,
                fontSize: 12
              }}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default BarGraph;